/**
 * Session resume (crash / tab-close recovery).
 *
 * While a study is open, the viewer continuously snapshots WHERE the reader is —
 * the viewer URL (studies + hanging protocol) and, per pane, the series shown,
 * the slice index and the window/level — into localStorage. If the tab crashes
 * or is closed mid-read, the worklist shows a "Resume last session" pill.
 *
 * Restore is ALWAYS explicit (the pill sets a one-shot flag consumed by the
 * viewer route): opening a new patient normally is never hijacked.
 */

export interface SavedPane {
  /** SeriesInstanceUIDs shown in this pane (stable across sessions, unlike displaySet UIDs) */
  series: string[];
  imageIndex?: number;
  /** SOPInstanceUID of the exact slice shown — lets the reopen deep-link to it via
   * the hanging protocol's initialSOPInstanceUID (opens AT the slice, no post-hoc jump). */
  sopInstanceUID?: string;
  voiRange?: { lower: number; upper: number };
}

export interface SavedSession {
  v: 1;
  ts: number;
  /** pathname + search of the viewer route to reopen */
  url: string;
  studyUIDs: string[];
  patientName?: string;
  studyDescription?: string;
  panes: SavedPane[];
}

const KEY = 'astra:lastSession';
const RESUME_FLAG = 'astra:resumeRequested';
/** Sessions older than this are not offered for resume. */
export const RESUME_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function saveSession(session: Omit<SavedSession, 'v' | 'ts'>): void {
  try {
    // Never clobber a meaningful snapshot with an empty one (e.g. while panes
    // are still hydrating right after load).
    if (!session.studyUIDs?.length || !session.panes?.some(p => p.series?.length)) {
      return;
    }
    const payload: SavedSession = { v: 1, ts: Date.now(), ...session };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch (e) {
    /* storage full/blocked — resume is best-effort */
  }
}

export function loadSession(): SavedSession | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (parsed?.v !== 1 || !parsed.url || !Array.isArray(parsed.studyUIDs)) {
      return null;
    }
    return parsed as SavedSession;
  } catch (e) {
    return null;
  }
}

export function clearSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch (e) {
    /* ignore */
  }
}

/** True when the saved session is fresh enough to offer on the worklist. */
export function isResumable(session: SavedSession | null): session is SavedSession {
  return !!session && Date.now() - session.ts < RESUME_MAX_AGE_MS;
}

/** Set by the worklist Resume pill just before navigating. */
export function requestResume(): void {
  try {
    sessionStorage.setItem(RESUME_FLAG, '1');
  } catch (e) {
    /* ignore */
  }
}

/** One-shot: returns true (and clears the flag) if this load is a resume. */
export function consumeResumeRequest(): boolean {
  try {
    const v = sessionStorage.getItem(RESUME_FLAG) === '1';
    sessionStorage.removeItem(RESUME_FLAG);
    return v;
  } catch (e) {
    return false;
  }
}
